import React, { useMemo, useContext } from "react";
import { Box, Card, CardContent, Typography, Stack, Chip } from "@mui/material";
import { ConfirmationNumber as TicketIcon } from "@mui/icons-material";
import { WidgetContext } from "../../store/widgetContext";
import { formatDate, trimString } from "../../utils/common";
import { useWidgetMsg } from "../../hooks/widget/useWidgetMsg";

export default function Tickets({ searchQuery, handleNavigate }) {
	const { agents, tickets } = useContext(WidgetContext);
	const { msg } = useWidgetMsg();

	const getLocalizedTicketStatus = (status) => {
		switch (status) {
			case "Open":
				return msg("tickets.open", "Open");
			case "In progress":
				return msg("tickets.in_progress", "In progress");
			case "Closed":
				return msg("tickets.closed", "Closed");
			default:
				return status || "";
		}
	};

	const filteredTickets = useMemo(() => {
		if (!tickets || !Array.isArray(tickets)) {
			return [];
		}

		let filtered = tickets;

		if (searchQuery.trim()) {
			filtered = tickets.filter((ticket) => {
				const description = ticket.meta?.find(el => el.field === "Description");
				const descriptionText = description?.value || "";
				return descriptionText.toLowerCase().includes(searchQuery.toLowerCase());
			});
		}

		return filtered.sort((a, b) => {
			const dateA = new Date(a.updatedAt || a.createdAt);
			const dateB = new Date(b.updatedAt || b.createdAt);
			return dateB - dateA;
		});
	}, [tickets, searchQuery]);

	return (
		<Box
			sx={{
				width: "100%",
				height: "100%",
				display: "flex",
				flexDirection: "column",
				background: "white",
				borderRadius: "16px",
				overflow: "hidden",
			}}
		>
			<Box sx={{ flex: 1, overflowY: "auto", padding: "20px", background: "white" }}>
				{filteredTickets.length > 0 ? (
					<Stack spacing={1.5} sx={{ paddingBottom: "50px" }}>
						{filteredTickets.map((ticket, idx) => {
							const description = ticket.meta?.find(el => el.field === "Description");
							const assignedAgentId = ticket.agentIDaasId;
							const assignedAgent = agents?.find(agent => agent.agentIDaasId === assignedAgentId);

							return (
								<Card
									key={ticket._id || `ticket-${idx}`}
									sx={{
										background: "white",
										borderRadius: "16px",
										boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
										cursor: "pointer",
										"&:hover": { boxShadow: "0px 2px 6px rgba(0, 0, 0, 0.15)" },
									}}
									onClick={() => {
										if (handleNavigate && ticket.conversationId) {
											handleNavigate({
												location: "newConversation",
												conversationId: ticket.conversationId,
												assignedAgents: assignedAgent ? [assignedAgent] : [],
											});
										}
									}}
								>
									<CardContent sx={{ padding: "16px !important" }}>
										<Stack direction="row" spacing={1.5} alignItems="flex-start">
											{ticket.conversationId && (
												<Chip
													label={`#${ticket.ticketNumber || idx + 1}`}
													size="small"
													icon={<TicketIcon sx={{ fill: 'black', fontSize: 10 }} />}
													sx={{ height: 20, fontSize: "10px", background: "#EFF0F6" }}
												/>
											)}
											<Box sx={{ flex: 1, minWidth: 0 }}>
												<Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, justifyContent: "space-between" }}>
													<Typography variant="caption" sx={{ fontSize: "12px" }}>
														{formatDate(ticket.createdAt, msg)}
													</Typography>
													<Typography variant="caption" sx={{ fontSize: "12px" }}>
														{getLocalizedTicketStatus(ticket.status)}
													</Typography>
												</Stack>
												<Typography variant="body2" sx={{ fontSize: "12px", mb: 1 }}>
													{trimString(description?.value || "", 100) || "..."}
												</Typography>
											</Box>
										</Stack>
									</CardContent>
								</Card>
							);
						})}
					</Stack>
				) : (
					<Box
						sx={{
							display: "flex",
							flexDirection: "column",
							alignItems: "center",
							justifyContent: "center",
							minHeight: "200px",
							textAlign: "center",
						}}
					>
						<Typography variant="body2" sx={{ fontSize: "14px", color: "#808080" }}>
							{msg("tickets.no_recent_tickets", "No recent tickets")}
						</Typography>
					</Box>
				)}
			</Box>
		</Box>
	);
}

